<script>
  import Layout from '../components/Layout.svelte';
  import { onMount, tick } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { createCurrencyFormatter } from '../lib/currency.js';
  import { user } from '../stores/authStore';

  let allItems = [];

  // Bill settings & bank accounts for per-row transaction account selection
  let billSettings = {};
  let xeroBankAccounts = [];
  let billBankAccounts = {};

  // UI state
  let loading = true;
  let error = null;
  let selectedItemIDs = new Set();
  let hideSynced = true;
  let showAllTypes = true;
  let showPaid = true;
  let showDrafts = false;
  let searchQuery = "";
  let checkingSyncStatus = false;

  // Pagination state
  let currentPage = 1;
  let pageSize = 10;
  let invoicesNextPage = null;
  let isLoadingMoreInvoices = false;

  // Syncing state
  let syncing = false;
  let syncProgress = { current: 0, total: 0 };
  let syncResults = [];

  // Modal state for mark as synced confirmation
  let showMarkSyncedModal = false;
  let pendingMarkSyncedIds = [];

  let salesMappingBannerDismissed = false;
  let hadInvoiceCostCodeMappingToggle = false;

  let syncResultsSection;

  function isPaid(inv) {
    const total = inv.total || inv.priceWithTax || 0;
    const amountPaid = inv.amountPaid || 0;
    return total > 0 && amountPaid >= total;
  }

  function isDraft(item) {
    return item.sourceType === 'invoice' && item.status === 'draft';
  }

  // Reactive computed views
  $: showPaymentAccountCol = showAllTypes && xeroBankAccounts.length > 0;
  $: filteredItems = getFilteredItems(allItems, hideSynced, searchQuery, showAllTypes, showPaid, showDrafts);
  $: visibleItems = filteredItems.slice(0, currentPage * pageSize);
  $: totalFilteredCount = filteredItems.length;
  $: totalPages = Math.ceil(totalFilteredCount / pageSize);
  $: selectableItems = visibleItems.filter(item => !item.isSynced && !isDraft(item));
  $: allSelectableAreSelected = selectableItems.length > 0 &&
    selectableItems.every(item => selectedItemIDs.has(item.id));
  $: canSyncSelected = selectedItemIDs.size > 0 && !syncing;
  $: canMarkSelected = selectedItemIDs.size > 0 && !syncing;

  function getFilteredItems(items, hideSync, search, showAll, showPaidInvoices, showDraftInvoices) {
    let filtered = [...items];

    // When showAll is off, only show invoices
    if (!showAll) {
      filtered = filtered.filter(b => b.sourceType === 'invoice');
    }

    if (hideSync) {
      filtered = filtered.filter(b => !b.isSynced);
    }

    // Hide paid invoices unless toggle is on
    if (!showPaidInvoices) {
      filtered = filtered.filter(b => b.sourceType !== 'invoice' || !isPaid(b));
    }

    // Hide draft invoices unless toggle is on
    if (!showDraftInvoices) {
      filtered = filtered.filter(b => !isDraft(b));
    }

    if (search && search.trim().length >= 2) {
      const query = search.toLowerCase();
      filtered = filtered.filter(b =>
        (b.number && String(b.number).toLowerCase().includes(query)) ||
        (b.displayName && b.displayName.toLowerCase().includes(query)) ||
        (b.job?.name && b.job.name.toLowerCase().includes(query)) ||
        (b.subject && b.subject.toLowerCase().includes(query)) ||
        (b.description && b.description.toLowerCase().includes(query))
      );
    }

    // Bills and invoices arrive as two lists; sort them together or the
    // invoices end up behind the whole bill backlog.
    return filtered.sort((a, b) =>
      dateValue(displayDate(b)) - dateValue(displayDate(a)) ||
      dateValue(b.createdAt) - dateValue(a.createdAt)
    );
  }

  function displayDate(item) {
    return item.sourceType === 'invoice' ? (item.issueDate || item.createdAt) : (item.date || item.createdAt);
  }

  function dateValue(value) {
    const time = value ? new Date(value).getTime() : NaN;
    return Number.isNaN(time) ? 0 : time;
  }

  async function syncItemWithRetry(syncFn, maxRetries = 3) {
    let lastResponse;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      lastResponse = await syncFn();
      if (lastResponse.ok) return lastResponse;
      if (lastResponse.status === 429 && attempt < maxRetries) {
        const data = await lastResponse.json().catch(() => ({}));
        const wait = (data.retryAfter || 60) * 1000;
        console.warn(`Rate limited (429), retrying in ${wait}ms (attempt ${attempt}/${maxRetries})`);
        await new Promise(r => setTimeout(r, wait));
        continue;
      }
      return lastResponse;
    }
    return lastResponse;
  }

  async function fetchData() {
    try {
      loading = true;
      error = null;

      const billParams = new URLSearchParams();
      billParams.set('status', 'approved');
      billParams.set('size', '100');
      billParams.set('maxTotal', '500');

      const invoiceParams = new URLSearchParams({ days: '30' });
      if (showDrafts) invoiceParams.set('status', 'draft,pending,approved');

      const [billsResponse, invoicesResponse] = await Promise.all([
        fetch(`/api/jt/vendor-bills?${billParams.toString()}`),
        fetch(`/api/jt/invoices-all?${invoiceParams.toString()}`)
      ]);

      const billsData = await billsResponse.json();
      const invoicesData = await invoicesResponse.json();

      if (!billsResponse.ok) {
        if (billsResponse.status === 401 && billsData.needsLogin) {
          error = 'Please configure your JobTread credentials in Settings';
          return;
        }
        throw new Error(billsData.error || 'Failed to fetch bills');
      }

      if (!billsData.success || !Array.isArray(billsData.bills)) {
        throw new Error('Invalid bill data format received from server');
      }

      const billItems = billsData.bills.map(bill => ({
        ...bill,
        sourceType: bill.itemType === 'expense' ? 'expense' : 'vendorBill',
        displayName: bill.account?.name || bill.vendor?.name || 'N/A',
        vendor: { id: bill.account?.id || bill.vendor?.id, name: bill.account?.name || bill.vendor?.name },
        job: bill.job || { id: null, name: null, number: null },
        isSynced: false,
        syncDetails: null,
        syncStatusChecked: false
      }));

      const invoiceItems = (invoicesData.invoices || []).map(inv => ({
        ...inv,
        sourceType: 'invoice',
        status: inv.status,
        displayName: inv.customer || inv.account?.name || 'N/A',
        number: `${inv.jobNumber}-${inv.number}`,
        _rawNumber: inv.number,
        _jobNumber: inv.jobNumber,
        vendor: null,
        job: { id: inv.jobId, name: inv.jobName, number: inv.jobNumber },
        total: inv.total || inv.priceWithTax || 0,
        isSynced: false,
        syncDetails: null,
        syncStatusChecked: false,
        needsSync: inv.status !== 'draft'
      }));

      invoicesNextPage = invoicesData.nextPage || null;

      allItems = [...billItems, ...invoiceItems];
      console.log(`Fetched ${billItems.length} bills/expenses + ${invoiceItems.length} invoices`);

      await checkAllSyncStatuses();

    } catch (err) {
      console.error('Error fetching data:', err);
      error = err.message;
    } finally {
      loading = false;
    }
  }

  async function checkAllSyncStatuses() {
    if (allItems.length === 0) return;

    checkingSyncStatus = true;

    try {
      const billIds = allItems.filter(b => b.sourceType === 'vendorBill').map(b => b.id);
      const expenseIds = allItems.filter(b => b.sourceType === 'expense').map(b => b.id);
      const invoiceItems = allItems.filter(b => b.sourceType === 'invoice');

      const promises = [];

      if (billIds.length > 0 || expenseIds.length > 0) {
        promises.push(
          fetch('/api/jt/check-vendor-bills-sync-status', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ billIds, expenseIds })
          }).then(r => r.ok ? r.json() : { syncStatuses: {} })
        );
      } else {
        promises.push(Promise.resolve({ syncStatuses: {} }));
      }

      if (invoiceItems.length > 0) {
        const invoicesForCheck = invoiceItems.map(inv => ({
          id: inv.id,
          jobNumber: inv._jobNumber || inv.job?.number,
          number: inv._rawNumber
        }));
        promises.push(
          fetch('/api/jt/invoices/check-sync-status', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ invoices: invoicesForCheck })
          }).then(r => r.ok ? r.json() : { syncStatuses: {} })
        );
      } else {
        promises.push(Promise.resolve({ syncStatuses: {} }));
      }

      const [billSyncData, invoiceSyncData] = await Promise.all(promises);

      const billStatuses = billSyncData.syncStatuses || {};
      const invoiceStatuses = invoiceSyncData.syncStatuses || {};

      allItems = allItems.map(item => {
        if (item.sourceType === 'invoice') {
          const status = invoiceStatuses[item.id];
          return {
            ...item,
            isSynced: status?.isSynced || false,
            syncDetails: status?.isSynced ? {
              xeroInvoiceId: status.xeroInvoiceId,
              xeroInvoiceNumber: status.xeroInvoiceNumber,
              source: status.source
            } : null,
            needsSync: item.status !== 'draft' && !status?.isSynced,
            syncStatusChecked: true
          };
        } else {
          const status = billStatuses[item.id];
          return {
            ...item,
            isSynced: status?.isSynced || false,
            syncDetails: status?.isSynced ? {
              xeroBillId: status.targetId,
              xeroBillNumber: status.targetNumber,
              syncedAt: status.syncedAt
            } : null,
            needsSync: !status?.isSynced,
            syncStatusChecked: true
          };
        }
      });

      const syncedCount = allItems.filter(b => b.isSynced).length;
      console.log(`Sync check complete: ${syncedCount} synced, ${allItems.length - syncedCount} need sync`);
      initBillBankAccountDefaults();
    } catch (err) {
      console.error('Error checking sync status:', err);
    } finally {
      checkingSyncStatus = false;
    }
  }

  async function loadMoreInvoices() {
    if (!invoicesNextPage || isLoadingMoreInvoices) return;

    isLoadingMoreInvoices = true;
    try {
      const params = new URLSearchParams();
      params.append('days', '30');
      if (showDrafts) params.append('status', 'draft,pending,approved');
      params.append('page', invoicesNextPage);

      const response = await fetch(`/api/jt/invoices-all?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to fetch more invoices');

      const data = await response.json();
      const existingIds = new Set(allItems.map(i => i.id));

      const newInvoices = (data.invoices || [])
        .filter(inv => !existingIds.has(inv.id))
        .map(inv => ({
          ...inv,
          sourceType: 'invoice',
          status: inv.status,
          displayName: inv.customer || inv.account?.name || 'N/A',
          number: `${inv.jobNumber}-${inv.number}`,
          _rawNumber: inv.number,
          _jobNumber: inv.jobNumber,
          vendor: null,
          job: { id: inv.jobId, name: inv.jobName, number: inv.jobNumber },
          total: inv.total || inv.priceWithTax || 0,
          isSynced: false,
          syncDetails: null,
          syncStatusChecked: false,
          needsSync: inv.status !== 'draft'
        }));

      invoicesNextPage = data.nextPage || null;

      if (newInvoices.length > 0) {
        const invoicesForCheck = newInvoices.map(inv => ({
          id: inv.id,
          jobNumber: inv._jobNumber,
          number: inv._rawNumber
        }));

        const syncResponse = await fetch('/api/jt/invoices/check-sync-status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ invoices: invoicesForCheck })
        });

        if (syncResponse.ok) {
          const syncData = await syncResponse.json();
          const syncStatuses = syncData.syncStatuses || {};

          const newInvoicesWithStatus = newInvoices.map(inv => {
            const status = syncStatuses[inv.id];
            return {
              ...inv,
              isSynced: status?.isSynced || false,
              syncDetails: status?.isSynced ? {
                xeroInvoiceId: status.xeroInvoiceId,
                xeroInvoiceNumber: status.xeroInvoiceNumber,
                source: status.source
              } : null,
              needsSync: inv.status !== 'draft' && !status?.isSynced,
              syncStatusChecked: true
            };
          });
          allItems = [...allItems, ...newInvoicesWithStatus];
        } else {
          allItems = [...allItems, ...newInvoices];
        }
      }

      if (!data.nextPage || (data.invoices || []).length === 0) {
        invoicesNextPage = null;
      }
    } catch (err) {
      console.error('Error loading more invoices:', err);
    } finally {
      isLoadingMoreInvoices = false;
    }
  }

  function handleSearch(event) {
    searchQuery = event.target.value;
    currentPage = 1;
  }

  function handleRetry() {
    window.location.reload();
  }

  async function loadBillSettings() {
    try {
      const response = await fetch('/api/users/integrations');
      if (response.ok) {
        const data = await response.json();
        billSettings = data.billSettings || {};
        showAllTypes = billSettings.showAllTypes ?? true;
      }
    } catch (err) {
      console.warn('Could not load bill settings:', err);
    }
  }

  async function loadXeroBankAccounts() {
    try {
      const response = await fetch('/api/xero/bank-accounts');
      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.accounts)) {
          xeroBankAccounts = data.accounts;
        }
      }
    } catch (err) {
      console.warn('Could not load Xero bank accounts:', err);
    }
  }

  function initBillBankAccountDefaults() {
    const defaultAccountID = billSettings.xeroExpenseBankAccountID || xeroBankAccounts[0]?.accountID || '';
    allItems.forEach(b => {
      if (b.sourceType === 'expense' && !b.isSynced && !billBankAccounts[b.id]) {
        billBankAccounts[b.id] = defaultAccountID;
      }
    });
    billBankAccounts = { ...billBankAccounts };
  }

  async function handleShowDraftsChange() {
    selectedItemIDs = new Set();
    currentPage = 1;
    await fetchData();
  }

  async function handleShowAllTypesChange(event) {
    const checked = event.target.checked;
    showAllTypes = checked;
    try {
      const response = await fetch('/api/users/bill-settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ showAllTypes: checked })
      });
      if (!response.ok) {
        console.error('Failed to save showAllTypes setting');
        showAllTypes = !checked;
      }
    } catch (err) {
      console.error('Failed to save showAllTypes setting:', err);
      showAllTypes = !checked;
    }
  }

  async function loadSalesMappingBannerState() {
    try {
      const response = await fetch('/api/user');
      const data = await response.json();
      if (response.ok && data.success && data.user) {
        salesMappingBannerDismissed = !!data.user.salesMappingBannerDismissed;
        hadInvoiceCostCodeMappingToggle = !!data.user.useCostCodeMappingsForInvoices;
      }
    } catch (err) {
      console.error('Error loading sales mapping banner state:', err);
    }
  }

  async function dismissSalesMappingBanner() {
    salesMappingBannerDismissed = true;
    try {
      await fetch('/api/users/sales-mapping-banner-dismissed', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dismissed: true })
      });
    } catch (err) {
      console.error('Failed to dismiss sales mapping banner:', err);
    }
  }

  onMount(async () => {
    await Promise.all([loadBillSettings(), loadXeroBankAccounts(), loadSalesMappingBannerState()]);
    await fetchData();
    initBillBankAccountDefaults();
  });

  function formatDate(dateString) {
    if (!dateString) return '—';
    const str = String(dateString);
    const date = str.includes('T') ? new Date(str) : new Date(str + 'T00:00:00');
    return date.toLocaleDateString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  $: formatCurrency = createCurrencyFormatter($user?.country);

  function toggleItemSelection(itemID) {
    if (itemID === undefined) return;
    selectedItemIDs = new Set(selectedItemIDs);
    if (selectedItemIDs.has(itemID)) {
      selectedItemIDs.delete(itemID);
    } else {
      selectedItemIDs.add(itemID);
    }
  }

  function handleRowClick(item) {
    if (item.isSynced || isDraft(item)) return;
    toggleItemSelection(item.id);
  }

  function serializeInvoiceForSync(inv) {
    return {
      id: inv.id,
      status: inv.status,
      amountPaid: inv.amountPaid,
      number: inv._rawNumber,
      customer: inv.displayName,
      location: inv.location || inv.jobLocationAddress,
      subject: inv.subject,
      issueDate: inv.issueDate,
      createdAt: inv.createdAt,
      dueDate: inv.dueDate,
      jobId: inv.job?.id,
      jobName: inv.job?.name,
      jobNumber: inv._jobNumber || inv.job?.number,
      total: inv.total || inv.priceWithTax,
      lineItems: (inv.lineItems || []).map(item => ({
        name: item.name || item.description,
        description: item.description || item.name,
        quantity: item.quantity || 1,
        unitPrice: item.unitPrice || (item.price / (item.quantity || 1)),
        price: item.price,
        priceWithTax: item.priceWithTax,
        unit: item.unit
      }))
    };
  }

  async function syncSelected() {
    const selectedItems = visibleItems.filter(b => selectedItemIDs.has(b.id));
    const selectedBills = selectedItems.filter(b => b.sourceType === 'vendorBill');
    const selectedExpenses = selectedItems.filter(b => b.sourceType === 'expense');
    const selectedInvoices = selectedItems.filter(b => b.sourceType === 'invoice');

    if (selectedItems.length === 0) return;

    syncing = true;
    syncProgress = { current: 0, total: selectedBills.length + selectedExpenses.length + selectedInvoices.length };
    syncResults = [];

    await tick();
    if (syncResultsSection) {
      syncResultsSection.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    try {
      // Phase 1: Bills
      for (let i = 0; i < selectedBills.length; i++) {
        const bill = selectedBills[i];
        try {
          const response = await syncItemWithRetry(() =>
            fetch('/api/jt/sync-vendor-bill-to-xero', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ jtBillId: bill.id })
            })
          );

          const result = await response.json();

          if (response.ok && result.success) {
            syncResults.push({ item: bill, sourceType: 'vendorBill', success: true, xeroBill: result.xeroBill, mappingsUsed: result.mappingsUsed || {} });
            const idx = allItems.findIndex(b => b.id === bill.id);
            if (idx !== -1) {
              allItems[idx] = { ...allItems[idx], isSynced: true, syncStatusChecked: true, needsSync: false, syncDetails: { xeroBillId: result.xeroBill.id, xeroBillNumber: result.xeroBill.number, syncedAt: new Date().toISOString() } };
              allItems = [...allItems];
            }
          } else {
            syncResults.push({ item: bill, sourceType: 'vendorBill', success: false, error: result.error || 'Unknown error', duplicateSupplierInvoice: result.duplicateSupplierInvoice === true, retryBody: { jtBillId: bill.id } });
          }
        } catch (err) {
          syncResults.push({ item: bill, sourceType: 'vendorBill', success: false, error: err.message });
        }

        syncProgress.current++;
        syncResults = [...syncResults];
        if (i < selectedBills.length - 1) await new Promise(r => setTimeout(r, 40));
      }

      // Phase 2: Expenses
      for (let i = 0; i < selectedExpenses.length; i++) {
        const expense = selectedExpenses[i];
        try {
          const response = await syncItemWithRetry(() =>
            fetch('/api/jt/sync-vendor-bill-to-xero', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ jtBillId: expense.id, itemType: 'expense', bankAccountID: billBankAccounts[expense.id] || '' })
            })
          );

          const result = await response.json();

          if (response.ok && result.success) {
            syncResults.push({ item: expense, sourceType: 'expense', success: true, xeroBill: result.xeroBill, mappingsUsed: result.mappingsUsed || {} });
            const idx = allItems.findIndex(b => b.id === expense.id);
            if (idx !== -1) {
              allItems[idx] = { ...allItems[idx], isSynced: true, syncStatusChecked: true, needsSync: false, syncDetails: { xeroBillId: result.xeroBill.id, xeroBillNumber: result.xeroBill.number, syncedAt: new Date().toISOString() } };
              allItems = [...allItems];
            }
          } else {
            syncResults.push({ item: expense, sourceType: 'expense', success: false, error: result.error || 'Unknown error', duplicateSupplierInvoice: result.duplicateSupplierInvoice === true, retryBody: { jtBillId: expense.id, itemType: 'expense', bankAccountID: billBankAccounts[expense.id] || '' } });
          }
        } catch (err) {
          syncResults.push({ item: expense, sourceType: 'expense', success: false, error: err.message });
        }

        syncProgress.current++;
        syncResults = [...syncResults];
        if (i < selectedExpenses.length - 1) await new Promise(r => setTimeout(r, 40));
      }

      // Phase 3: Invoices
      for (let i = 0; i < selectedInvoices.length; i++) {
        const invoice = selectedInvoices[i];
        try {
          const invoicePayload = serializeInvoiceForSync(invoice);

          const response = await syncItemWithRetry(() =>
            fetch('/api/xero/create-invoice', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(invoicePayload)
            })
          );

          if (response.ok) {
            const result = await response.json();
            syncResults.push({
              item: invoice,
              sourceType: 'invoice',
              success: true,
              invoiceNumber: result.invoiceNumber,
              invoiceID: result.invoiceID,
              xeroUrl: result.xeroUrl,
              warning: result.warning
            });
            const idx = allItems.findIndex(b => b.id === invoice.id);
            if (idx !== -1) {
              allItems[idx] = { ...allItems[idx], isSynced: true, syncStatusChecked: true, needsSync: false, syncDetails: { xeroInvoiceId: result.invoiceID, xeroInvoiceNumber: result.invoiceNumber } };
              allItems = [...allItems];
            }
          } else {
            let errorMessage = 'Failed to sync invoice';
            let duplicateNumber = false;
            try {
              const errorData = await response.json();
              errorMessage = errorData.message || errorData.error || errorMessage;
              duplicateNumber = errorData.duplicateNumber === true;
            } catch (_) {}
            // Xero already has this number: offer "Create anyway" (server names it -a).
            syncResults.push({ item: invoice, sourceType: 'invoice', success: false, error: errorMessage, duplicateNumber, payload: invoicePayload });
          }
        } catch (err) {
          syncResults.push({ item: invoice, sourceType: 'invoice', success: false, error: err.message });
        }

        syncProgress.current++;
        syncResults = [...syncResults];
        if (i < selectedInvoices.length - 1) await new Promise(r => setTimeout(r, 40));
      }
    } finally {
      syncing = false;
      selectedItemIDs = new Set();
    }
  }

  // The user has checked the Xero invoice with the same number and says this
  // one is genuinely separate: create it anyway (numbered "-a" by the server).
  async function createInvoiceAnyway(result) {
    result.creating = true;
    syncResults = [...syncResults];
    try {
      const response = await fetch('/api/xero/create-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...result.payload, allowDuplicateNumber: true })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || data.error || 'Failed to sync invoice');
      Object.assign(result, { success: true, error: null, duplicateNumber: false, invoiceNumber: data.invoiceNumber, invoiceID: data.invoiceID, xeroUrl: data.xeroUrl, warning: data.warning });
      const idx = allItems.findIndex(b => b.id === result.item.id);
      if (idx !== -1) {
        allItems[idx] = { ...allItems[idx], isSynced: true, syncStatusChecked: true, needsSync: false, syncDetails: { xeroInvoiceId: data.invoiceID, xeroInvoiceNumber: data.invoiceNumber } };
        allItems = [...allItems];
      }
    } catch (err) {
      result.error = err.message;
    } finally {
      result.creating = false;
      syncResults = [...syncResults];
    }
  }

  // Xero already has a bill from this supplier with this invoice number; the
  // user has checked it and says this one is different: create it anyway.
  async function createBillAnyway(result) {
    result.creating = true;
    syncResults = [...syncResults];
    try {
      const response = await fetch('/api/jt/sync-vendor-bill-to-xero', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...result.retryBody, allowDuplicateSupplierInvoice: true })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) throw new Error(data.error || 'Failed to sync to Xero');
      Object.assign(result, { success: true, error: null, duplicateSupplierInvoice: false, xeroBill: data.xeroBill, mappingsUsed: data.mappingsUsed || {} });
      const idx = allItems.findIndex(b => b.id === result.item.id);
      if (idx !== -1) {
        allItems[idx] = { ...allItems[idx], isSynced: true, syncStatusChecked: true, needsSync: false, syncDetails: { xeroBillId: data.xeroBill.id, xeroBillNumber: data.xeroBill.number, syncedAt: new Date().toISOString() } };
        allItems = [...allItems];
      }
    } catch (err) {
      result.error = err.message;
    } finally {
      result.creating = false;
      syncResults = [...syncResults];
    }
  }

  function markSelectedAsSynced() {
    const selectedArray = Array.from(selectedItemIDs);
    if (selectedArray.length === 0) return;
    pendingMarkSyncedIds = selectedArray;
    showMarkSyncedModal = true;
  }

  async function confirmMarkAsSynced() {
    showMarkSyncedModal = false;
    if (pendingMarkSyncedIds.length === 0) return;

    try {
      const billExpenseIds = pendingMarkSyncedIds.filter(id => {
        const item = allItems.find(b => b.id === id);
        return item && item.sourceType !== 'invoice';
      });
      const invoiceIds = pendingMarkSyncedIds.filter(id => {
        const item = allItems.find(b => b.id === id);
        return item && item.sourceType === 'invoice';
      });

      const promises = [];

      if (billExpenseIds.length > 0) {
        const items = billExpenseIds.map(id => {
          const item = allItems.find(b => b.id === id);
          return { id, itemType: item?.sourceType || 'vendorBill' };
        });
        promises.push(
          fetch('/api/jt/mark-bills-as-synced', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items })
          })
        );
      }

      if (invoiceIds.length > 0) {
        promises.push(
          fetch('/api/jt/mark-invoices-as-synced', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ invoiceIds })
          })
        );
      }

      const results = await Promise.all(promises);
      const allOk = results.every(r => r.ok);

      if (allOk) {
        allItems = allItems.map(item => {
          if (pendingMarkSyncedIds.includes(item.id)) {
            return { ...item, isSynced: true, needsSync: false, syncStatusChecked: true };
          }
          return item;
        });
        selectedItemIDs = new Set();
        allItems = [...allItems];
      } else {
        alert('Failed to mark some items as synced.');
      }
    } catch (err) {
      alert(`Error marking items as synced: ${err.message}`);
    } finally {
      pendingMarkSyncedIds = [];
    }
  }

  function cancelMarkAsSynced() {
    showMarkSyncedModal = false;
    pendingMarkSyncedIds = [];
  }

  function handleModalBackdropClick(e) {
    if (e.target === e.currentTarget) {
      cancelMarkAsSynced();
    }
  }

  function toggleSelectAll() {
    if (allSelectableAreSelected) {
      selectableItems.forEach(item => selectedItemIDs.delete(item.id));
    } else {
      selectableItems.forEach(item => selectedItemIDs.add(item.id));
    }
    selectedItemIDs = new Set(selectedItemIDs);
  }

  async function handleSyncAllClick() {
    const allOnPageNeedingSync = selectableItems;
    if (allOnPageNeedingSync.length === 0) {
      alert('No items on this page need syncing.');
      return;
    }
    allOnPageNeedingSync.forEach(item => selectedItemIDs.add(item.id));
    selectedItemIDs = new Set(selectedItemIDs);
    await syncSelected();
  }
</script>

<Layout activeRoute="import-from-jobtread">
  <div class="container mx-auto px-4 py-8">
    <h1 class="text-2xl font-bold mb-6">Import from JobTread to Xero</h1>
    <p class="text-sm text-gray-600 mb-4">Sync Vendor Bills, Expenses, and Invoices from JobTread</p>

    {#if !salesMappingBannerDismissed}
      <div class="mb-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded relative pr-10">
        <button
          type="button"
          on:click={dismissSalesMappingBanner}
          class="absolute top-3 right-3 text-blue-700 hover:text-blue-900 p-1 rounded hover:bg-blue-100"
          aria-label="Dismiss"
          title="Dismiss"
        >
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <p class="text-sm text-blue-900">
          <strong>New</strong> — Updated Cost code to Xero Chart of Accounts Mappings for invoices are available in
          <a href="/settings/xero-mapping" class="text-blue-700 underline hover:text-blue-900 font-medium">Settings</a>.
        </p>
        {#if hadInvoiceCostCodeMappingToggle}
          <p class="text-sm text-blue-800 mt-2">
            Your previous mapping for bills and invoice has been preserved but you can now create separate mappings for invoices.
          </p>
        {/if}
      </div>
    {/if}

    {#if loading}
      <div class="flex justify-center items-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    {:else if error}
      <div class="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded flex items-center justify-between">
        <span class="text-sm">{error}</span>
        <button
          on:click={handleRetry}
          class="ml-4 bg-blue-600 text-white text-sm px-3 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          Retry
        </button>
      </div>
    {:else if allItems.length === 0}
      <div class="flex items-center justify-between bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded">
        <p class="text-sm">No items found in JobTread.</p>
        <button
          on:click={handleRetry}
          class="ml-4 bg-blue-600 text-white text-sm px-3 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          Refresh
        </button>
      </div>
    {:else}
      <div class="bg-white rounded-lg shadow overflow-hidden">
        <div class="p-4 border-b">
          <div class="flex items-center justify-end flex-wrap gap-2 mb-3">
            <button
              on:click={markSelectedAsSynced}
              disabled={!canMarkSelected}
              class="inline-flex items-center justify-center h-9 px-4 text-sm font-medium rounded-md whitespace-nowrap transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400 bg-white text-gray-600 border border-gray-300 hover:bg-[#f0f4ff] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Mark as Synced ({selectedItemIDs.size})
            </button>
            <button
              on:click={syncSelected}
              disabled={!canSyncSelected}
              class="inline-flex items-center justify-center h-9 px-4 text-sm font-medium rounded-md whitespace-nowrap transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {syncing ? `Syncing ${syncProgress.current}/${syncProgress.total}…` : `Sync Selected (${selectedItemIDs.size})`}
            </button>
            <button
              on:click={handleSyncAllClick}
              disabled={syncing}
              class="inline-flex items-center justify-center h-9 px-4 text-sm font-medium rounded-md whitespace-nowrap transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-500 bg-green-600 text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Sync All
            </button>
          </div>
          <div class="flex items-center gap-6 flex-wrap">
            <label class="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                bind:checked={hideSynced}
                class="sr-only peer focus:outline-none focus-visible:outline-none focus:ring-0"
              >
              <div class="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              <span class="ms-3 text-sm font-medium text-gray-900">
                Hide Synced
                <span class="text-xs text-gray-500 font-normal">
                  ({allItems.filter(b => b.isSynced).length} synced, {allItems.filter(b => !b.isSynced).length} need sync)
                </span>
              </span>
            </label>
            <label class="inline-flex items-center cursor-pointer gap-2">
              <span class="text-sm font-medium {!showAllTypes ? 'text-gray-900' : 'text-gray-500'}">Show Invoices Only</span>
              <input
                type="checkbox"
                checked={showAllTypes}
                on:change={handleShowAllTypesChange}
                class="sr-only peer focus:outline-none focus-visible:outline-none focus:ring-0"
              >
              <div class="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              <span class="text-sm font-medium {showAllTypes ? 'text-gray-900' : 'text-gray-500'}">Show Bills, Expenses and Invoices</span>
            </label>
            <label class="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                bind:checked={showPaid}
                class="sr-only peer focus:outline-none focus-visible:outline-none focus:ring-0"
              />
              <div class="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              <span class="ms-3 text-sm font-medium text-gray-900">Include Paid</span>
            </label>
            <label class="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                bind:checked={showDrafts}
                on:change={handleShowDraftsChange}
                class="sr-only peer focus:outline-none focus-visible:outline-none focus:ring-0"
              />
              <div class="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              <span class="ms-3 text-sm font-medium text-gray-900">Include Drafts</span>
            </label>
            <a href="/sync-invoices" class="text-sm text-blue-600 hover:text-blue-800 hover:underline">Need to sync invoices older than 30 days?</a>
          </div>
        </div>

        <!-- Search -->
        <div class="p-4 pb-8 border-b bg-gray-50">
          <div class="relative">
            <div class="relative">
              <svg class="absolute left-3 top-2.5 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search by number, vendor/customer, job, or description (min. 2 chars)..."
                class="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm {searchQuery && searchQuery.trim().length === 1 ? 'border-yellow-400' : ''}"
                value={searchQuery}
                on:input={handleSearch}
              />
              {#if searchQuery && searchQuery.trim().length === 1}
                <div class="absolute -bottom-6 left-0 text-xs text-yellow-600">
                  Please enter at least 2 characters to search
                </div>
              {/if}
            </div>
            {#if searchQuery}
              <button
                class="absolute right-3 top-2 text-gray-400 hover:text-gray-600"
                on:click={() => { searchQuery = ""; currentPage = 1; }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
              </button>
            {/if}
          </div>
        </div>

        <!-- Status Info -->
        <div class="flex items-center justify-center px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
          <p class="text-sm text-gray-700">
            Showing
            <span class="font-medium">{visibleItems.length}</span>
            of
            <span class="font-medium">{totalFilteredCount}</span>
            results
          </p>
        </div>

        <!-- Select All -->
        <div class="px-4 py-3 bg-white border-b border-gray-200">
          <div class="flex items-center">
            <input
              type="checkbox"
              class="form-checkbox h-5 w-5 text-primary"
              checked={allSelectableAreSelected}
              on:change={toggleSelectAll}
              disabled={selectableItems.length === 0}
            >
            <span class="ml-2 text-gray-700">Select All</span>
          </div>
        </div>

        {#if visibleItems.length === 0}
          <div class="p-8 text-center text-gray-500">
            <p class="text-lg">
              {#if searchQuery}
                No items match your search for "{searchQuery}"
              {:else if hideSynced && allItems.every(b => b.isSynced)}
                All items are synced to Xero
              {:else}
                No items found needing sync
              {/if}
            </p>
          </div>
        {:else}
          <div class="overflow-x-auto">
            <table class="min-w-full table-fixed">
              <thead class="bg-white">
                <tr>
                  <th class="w-[4%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Select</th>
                  <th class="w-[7%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Type</th>
                  <th class="w-[10%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Number</th>
                  <th class="w-[17%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Vendor / Customer</th>
                  <th class="w-[14%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Job</th>
                  <th class="w-[8%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Date</th>
                  <th class="w-[8%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Due Date</th>
                  <th class="w-[8%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Total</th>
                  {#if showAllTypes}
                  <th class="w-[6%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Method</th>
                  {/if}
                  {#if showPaymentAccountCol}
                  <th class="w-[12%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Xero Transaction Acc</th>
                  {/if}
                  <th class="w-[8%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Sync Status</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-[#edf0f7]">
                {#each visibleItems as item (item.id)}
                <tr class="hover:bg-[#f0f4ff] {item.isSynced ? 'bg-gray-50 opacity-75' : ''} {isDraft(item) ? 'opacity-60' : 'cursor-pointer'}" on:click={() => handleRowClick(item)}>
                  <td class="px-2 py-3 whitespace-nowrap text-sm" on:click={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      class="form-checkbox h-4 w-4 text-primary {item.isSynced || isDraft(item) ? 'opacity-50 cursor-not-allowed' : ''}"
                      checked={selectedItemIDs.has(item.id)}
                      on:change={() => toggleItemSelection(item.id)}
                      disabled={item.isSynced || isDraft(item)}
                    />
                  </td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm">
                    {#if item.sourceType === 'expense'}
                      <span class="px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">Expense</span>
                    {:else if item.sourceType === 'invoice'}
                      <span class="px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Invoice</span>
                    {:else}
                      <span class="px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">Bill</span>
                    {/if}
                  </td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm truncate">{item.number || 'N/A'}</td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm truncate">{item.displayName || 'N/A'}</td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm truncate">{item.job?.name || '—'}</td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm">{formatDate(displayDate(item))}</td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm">{item.dueDate ? formatDate(item.dueDate) : '—'}</td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm">{formatCurrency(item.total || 0)}</td>
                  {#if showAllTypes}
                  <td class="px-2 py-3 whitespace-nowrap text-sm">
                    {#if item.sourceType === 'expense' && item.paymentMethod}
                      <span class="text-gray-700">{item.paymentMethod}</span>
                    {:else}
                      <span class="text-gray-400">—</span>
                    {/if}
                  </td>
                  {/if}
                  {#if showPaymentAccountCol}
                  <td class="px-2 py-3 text-sm" on:click={(e) => e.stopPropagation()}>
                    {#if item.sourceType === 'expense' && !item.isSynced}
                      <select
                        class="text-xs border border-gray-300 rounded px-1 py-1 w-full"
                        bind:value={billBankAccounts[item.id]}
                      >
                        {#each xeroBankAccounts as acct (acct.accountID)}
                          <option value={acct.accountID}>{acct.name}</option>
                        {/each}
                      </select>
                    {:else}
                      <span class="text-gray-400">—</span>
                    {/if}
                  </td>
                  {/if}
                  <td class="px-2 py-3 whitespace-nowrap text-sm">
                    {#if checkingSyncStatus && !item.syncStatusChecked}
                      <span class="px-1.5 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Checking...
                      </span>
                    {:else if item.isSynced}
                      <span class="px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        Synced
                      </span>
                    {:else}
                      <span class="px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        Needs Sync
                      </span>
                    {/if}
                    {#if item.sourceType === 'invoice' && isPaid(item)}
                      <span class="ml-1 px-1.5 inline-flex text-xs leading-5 font-medium rounded-full bg-blue-100 text-blue-800">
                        Paid
                      </span>
                    {/if}
                    {#if isDraft(item)}
                      <span class="ml-1 px-1.5 inline-flex text-xs leading-5 font-medium rounded-full bg-gray-100 text-gray-600">
                        Draft
                      </span>
                    {/if}
                  </td>
                </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}

        <!-- Show More / Load More Invoices -->
        <div class="flex items-center justify-center gap-4 px-4 py-4 bg-white border-t border-gray-200">
          {#if currentPage < totalPages}
            <button
              on:click={() => currentPage++}
              disabled={syncing}
              class="relative inline-flex items-center px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Show More ({Math.min(pageSize, totalFilteredCount - visibleItems.length)} more)
            </button>
          {/if}
          {#if invoicesNextPage}
            <button
              on:click={loadMoreInvoices}
              disabled={isLoadingMoreInvoices || syncing}
              class="relative inline-flex items-center px-6 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoadingMoreInvoices ? 'Loading...' : 'Load More Invoices'}
            </button>
          {/if}
        </div>

        <!-- Sync Progress Bar & Results -->
        {#if syncing || syncResults.length > 0}
          <div class="p-5 border-t bg-gray-50" bind:this={syncResultsSection}>
            {#if syncing}
              <div class="mb-5 bg-white rounded-lg border border-gray-200 p-4">
                <div class="flex items-center justify-between mb-2">
                  <span class="text-sm font-medium text-gray-700">Syncing to Xero</span>
                  <span class="text-sm font-semibold text-blue-600">{syncProgress.current} / {syncProgress.total}</span>
                </div>
                <div class="w-full bg-gray-100 rounded-full h-2">
                  <div
                    class="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style="width: {syncProgress.total > 0 ? (syncProgress.current / syncProgress.total) * 100 : 0}%"
                  ></div>
                </div>
              </div>
            {/if}

            <h3 class="text-base font-semibold text-gray-900 mb-3">Sync Results</h3>
            {#if syncResults.length === 0 && syncing}
              <div class="flex items-center justify-center py-6 text-gray-500">
                <div class="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent mr-3"></div>
                <span class="text-sm">Starting sync...</span>
              </div>
            {:else}
              <div class="space-y-2">
                {#each syncResults as result}
                {@const item = result.item}
                <div class="bg-white rounded-lg border {result.success ? 'border-green-200' : 'border-red-200'} overflow-hidden">
                  <div class="flex items-center">
                    <!-- Type pill column -->
                    <div class="flex-shrink-0 w-20 flex items-center justify-center py-3 {result.success ? 'bg-green-50' : 'bg-red-50'}">
                      {#if result.sourceType === 'expense'}
                        <span class="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-purple-100 text-purple-700">Expense</span>
                      {:else if result.sourceType === 'invoice'}
                        <span class="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-emerald-100 text-emerald-700">Invoice</span>
                      {:else}
                        <span class="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-blue-100 text-blue-700">Bill</span>
                      {/if}
                    </div>

                    <!-- Status icon -->
                    <div class="flex-shrink-0 w-8 flex items-center justify-center">
                      {#if result.success}
                        <svg class="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      {:else}
                        <svg class="h-4 w-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      {/if}
                    </div>

                    <!-- Content -->
                    <div class="flex-1 min-w-0 py-2.5 pr-2">
                      <div class="flex items-center gap-2">
                        <span class="text-sm font-medium text-gray-900 truncate">{item.number || item.id}</span>
                        <span class="text-sm text-gray-500 truncate">{item.displayName || ''}</span>
                      </div>
                      {#if result.success}
                        <div class="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                          {#if result.sourceType === 'invoice'}
                            <span class="text-xs text-gray-500">Xero: <span class="text-gray-700 font-medium">{result.invoiceNumber}</span></span>
                          {:else}
                            <span class="text-xs text-gray-500">Xero: <span class="text-gray-700 font-medium">{result.xeroBill.number}</span></span>
                          {/if}
                          {#if result.mappingsUsed && Object.keys(result.mappingsUsed).length > 0}
                            <span class="text-xs text-blue-600">
                              {Object.entries(result.mappingsUsed).map(([code, info]) =>
                                `${info.costCodeName} → ${info.xeroAccountCode}`
                              ).join(', ')}
                            </span>
                          {/if}
                          {#if result.xeroBill?.trackingCategory}
                            <span class="text-xs text-purple-600">{result.xeroBill.trackingCategory.name}: {result.xeroBill.trackingCategory.value}</span>
                          {/if}
                        </div>
                        {#if result.sourceType === 'invoice' && (item.total > 0) && ((item.amountPaid || 0) >= item.total)}
                          <div class="mt-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded inline-block">Paid in JT — synced as unpaid in Xero</div>
                        {/if}
                        {#if result.warning}
                          <div class="mt-1 text-xs text-red-600">{result.warning}</div>
                        {/if}
                      {:else}
                        <p class="text-xs text-red-600 mt-0.5">{result.error}</p>
                        {#if result.duplicateSupplierInvoice}
                          <button
                            class="mt-1 bg-white border border-red-300 text-red-700 hover:bg-red-50 text-xs px-2.5 py-0.5 rounded disabled:opacity-50"
                            disabled={result.creating}
                            on:click={() => createBillAnyway(result)}
                          >{result.creating ? 'Creating…' : 'Create anyway'}</button>
                        {/if}
                        {#if result.duplicateNumber}
                          <button
                            class="mt-1 bg-white border border-red-300 text-red-700 hover:bg-red-50 text-xs px-2.5 py-0.5 rounded disabled:opacity-50"
                            disabled={result.creating}
                            on:click={() => createInvoiceAnyway(result)}
                          >{result.creating ? 'Creating…' : 'Create anyway'}</button>
                        {/if}
                      {/if}
                    </div>

                    <!-- Action buttons -->
                    {#if result.success}
                      <div class="flex-shrink-0 flex items-center gap-1.5 pr-3">
                        {#if item.job?.id}
                          <a
                            href={result.sourceType === 'invoice'
                              ? `https://app.jobtread.com/jobs/${item.job.id}`
                              : `https://app.jobtread.com/jobs/${item.job.id}/documents/${item.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="inline-flex items-center gap-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 px-2.5 py-1.5 rounded-md transition-colors whitespace-nowrap"
                          >
                            JobTread
                            <svg class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        {/if}
                        <a
                          href={result.sourceType === 'invoice' ? result.xeroUrl : result.xeroBill?.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          class="inline-flex items-center gap-1 text-xs font-medium text-white bg-[#13B5EA] hover:bg-[#0fa3d4] px-2.5 py-1.5 rounded-md transition-colors whitespace-nowrap"
                        >
                          Xero
                          <svg class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    {/if}
                  </div>
                </div>
              {/each}
              </div>
            {/if}
          </div>
        {/if}
      </div>
    {/if}
  </div>

  <!-- Mark as Synced Confirmation Modal -->
  {#if showMarkSyncedModal}
    <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" on:click={handleModalBackdropClick} on:keydown={(e) => e.key === 'Escape' && cancelMarkAsSynced()} role="button" tabindex="-1">
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" role="dialog" aria-modal="true">
        <h3 class="text-lg font-semibold text-gray-900 mb-3">Mark as Synced</h3>
        <p class="text-gray-600 mb-4">
          Are you sure you want to mark <span class="font-semibold">{pendingMarkSyncedIds.length} item{pendingMarkSyncedIds.length > 1 ? 's' : ''}</span> as synced?
        </p>
        <p class="text-sm text-gray-500 mb-6">
          This will prevent them from appearing as "needing sync" in the future.
        </p>
        <div class="flex gap-3 justify-end">
          <button
            on:click={cancelMarkAsSynced}
            class="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            on:click={confirmMarkAsSynced}
            class="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
          >
            Mark as Synced
          </button>
        </div>
      </div>
    </div>
  {/if}
</Layout>

<style>
  @media (max-width: 1200px) {
    .table-fixed {
      table-layout: auto;
    }

    td {
      white-space: normal;
      word-break: break-word;
      font-size: 0.75rem;
      padding: 0.5rem 0.25rem;
    }

    th {
      white-space: nowrap !important;
      font-size: 0.75rem !important;
      padding: 0.5rem 0.25rem;
    }
  }
</style>
